import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslocoService } from '@jsverse/transloco';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { DataExchangeRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-dto';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { DataExchangeSubRequestTypeDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-type-dto';
import { DataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { PackagingSystemInfoDto } from 'src/app/data-transfer/entities/packaging-system-entities/packaging-system-info-dto';
import { PackagingUnitInfoDto } from 'src/app/data-transfer/entities/packaging-unit-entities/packaging-unit-info-dto';
import { PackagingComponentDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-dto';
import { MultiMaterialCompositeImportWrapper } from 'src/app/components/dialogs/selection-dialog-materials/selection-dialog-materials.component';
import { PathResolveService } from 'src/app/services/path-resolve.service';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';
import { UpdateDataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/update-data-exchange-request-status-dto';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { SubRequestCommentsDialogComponent, SubRequestCommentsDialogData, SubRequestCommentsDialogResult } from 'src/app/components/dialogs/sub-request-comments-dialog/sub-request-comments-dialog.component';
import { getDialogConfig } from 'src/app/util/dialog-util';

export interface RequestSubDataSources {
  systems: MatTableDataSource<PackagingSystemInfoDto>;
  units: MatTableDataSource<PackagingUnitInfoDto>;
  components: MatTableDataSource<PackagingComponentDto>;
  materials: MatTableDataSource<MultiMaterialCompositeImportWrapper>;
  systemStatusMap: Map<number, DataExchangeRequestStatusDto>;
  unitStatusMap: Map<number, DataExchangeRequestStatusDto>;
  componentStatusMap: Map<number, DataExchangeRequestStatusDto>;
  materialStatusMap: Map<number, DataExchangeRequestStatusDto>;
  systemCommentCountMap: Map<number, number>;
  unitCommentCountMap: Map<number, number>;
  componentCommentCountMap: Map<number, number>;
  materialCommentCountMap: Map<number, number>;
}

@Component({
  selector: 'app-data-exchange-request-table',
  templateUrl: './data-exchange-request-table.component.html',
  styleUrls: ['./data-exchange-request-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ])
  ]
})
export class DataExchangeRequestTableComponent implements OnChanges, AfterViewInit {

  readonly requestStatus = DataExchangeRequestStatusDto;

  @Input() dataSource!: MatTableDataSource<DataExchangeRequestDto>;
  @Input() isReceived = false;
  @Input() displayedColumns: string[] = [];
  @Input() systemColumns: string[] = [];
  @Input() unitColumns: string[] = [];
  @Input() componentColumns: string[] = [];
  @Input() materialColumns: string[] = [];

  @Output() dataReloadRequired = new EventEmitter<void>();

  @ViewChild(MatSort) sort!: MatSort;

  filteredColumns: string[] = [];
  expandedElement: DataExchangeRequestDto | null = null;
  private expandedElementId: number | null = null;
  private subDataSourcesCache = new Map<number, RequestSubDataSources>();

  constructor(
    private dataExchangeRequestApiService: DataExchangeRequestApiService,
    private router: Router,
    private componentTypeService: ComponentTypeService,
    private dialog: MatDialog,
    private translocoService: TranslocoService,
  ) {}

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'counterpart':
          return (this.isReceived ? item.sendingSecurityPrincipal?.name : item.receivingSecurityPrincipal?.name) ?? '';
        case 'subRequestCount':
          return item.subRequests?.length ?? 0;
        default:
          return (item as any)[property];
      }
    };
    this.setupDataSourceFilter(this.dataSource);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
      this.setupDataSourceFilter(this.dataSource);
    }
    if (changes['dataSource'] && !changes['dataSource'].firstChange && this.expandedElementId != null) {
      this.subDataSourcesCache.clear();
      const row = this.dataSource?.data.find(r => r.id === this.expandedElementId);
      if (row) {
        this.expandedElement = null;
        this.toggleRow(row);
      } else {
        this.expandedElement = null;
        this.expandedElementId = null;
      }
    }
  }

  getDate(dateStr: string): Date | null {
    if (dateStr == null) {
      return null;
    }
    return new Date(dateStr);
  }

  private setupDataSourceFilter(dataSource: MatTableDataSource<DataExchangeRequestDto>): void {
    dataSource.filterPredicate = (data: DataExchangeRequestDto, filter: any) => {
      if (!filter || !filter.criterion) { return true; }

      this.filteredColumns = Object.keys(filter.criterion);

      const resolveValue = (col: string): any => {
        switch (col) {
          case 'counterpart':
            return this.isReceived
              ? data.sendingSecurityPrincipal?.name
              : data.receivingSecurityPrincipal?.name;
          case 'subRequestCount':
            return data.subRequests?.length ?? 0;
          case 'status':
            return this.translocoService.translate(this.getStatusLabel(data.status));
          case 'createdAt':
            return this.getDate(data.createdAt);
          default:
            return (data as any)[col];
        }
      };

      for (const col of this.filteredColumns) {
        const criterion = filter.criterion[col];
        if (!criterion) { continue; }
        if (filter.allCriteria[criterion](resolveValue(col), filter.value[col]) === false) {
          return false;
        }
      }
      return true;
    };
  }

  getCounterpartOptions(): string[] {
    if (!this.dataSource?.data?.length) {
      return [];
    }

    const counterparts = this.dataSource.data
      .map(row => this.isReceived ? row.sendingSecurityPrincipal?.name : row.receivingSecurityPrincipal?.name)
      .filter((name): name is string => !!name);

    return [...new Set(counterparts)].sort((a, b) => a.localeCompare(b));
  }

  getStatusLabel(status: DataExchangeRequestStatusDto): string {
    const key = DataExchangeRequestStatusDto[status];
    return `dataManagement.dataExchangeRequests.status.${key}`;
  }

  getStatusOptions(): { name: string, value: string }[] {
    if (!this.dataSource?.data?.length) { return []; }
    const labels = [...new Set(
      this.dataSource.data.map(row => this.translocoService.translate(this.getStatusLabel(row.status)))
    )].sort();
    return labels.map(label => ({ name: label, value: label }));
  }

  allSubRequestsSubmitted(row: DataExchangeRequestDto): boolean {
    const subs = row.subRequests;
    return (subs?.length ?? 0) > 0 && subs!.every(s =>
      s.status === DataExchangeRequestStatusDto.Submitted ||
      s.status === DataExchangeRequestStatusDto.Closed
    ) && subs!.some(s => s.status === DataExchangeRequestStatusDto.Submitted);
  }

  toggleRow(row: DataExchangeRequestDto): void {
    this.expandedElement = this.expandedElement === row ? null : row;
    this.expandedElementId = this.expandedElement?.id ?? null;
    if (this.expandedElement) {
      if (!this.subDataSourcesCache.has(row.id)) {
        this.buildSubDataSources(row);
      }
      if (this.isReceived) {
        if (row.hasNotificationForReceiver) {
          row.hasNotificationForReceiver = false;
          this.dataExchangeRequestApiService.markReceivedNotificationAsSeen(row.id).subscribe(() => {
            this.dataExchangeRequestApiService.reloadUnseenDataExchangeRequests(true);
          });
        }
      } else {
        if (row.hasNotificationForSender) {
          row.hasNotificationForSender = false;
          this.dataExchangeRequestApiService.markSentNotificationAsSeen(row.id).subscribe(() => {
            this.dataExchangeRequestApiService.reloadUnseenDataExchangeRequests(true);
          });
        }
      }
    }
  }

  getSubDataSources(row: DataExchangeRequestDto): RequestSubDataSources | undefined {
    return this.subDataSourcesCache.get(row.id);
  }

  onCommentAdded(text: string, row: DataExchangeRequestDto): void {
    this.dataExchangeRequestApiService.addComment(row.id, text).subscribe({
      next: () => this.dataReloadRequired.emit()
    });
  }

  onWithdrawRequest(row: DataExchangeRequestDto): void {
    if (row.status === DataExchangeRequestStatusDto.Deleted) {
      return;
    }

    this.dataExchangeRequestApiService.withdrawDataExchangeRequest(row.id).subscribe({
      next: () => this.dataReloadRequired.emit()
    });
  }

  onApproveRequest(row: DataExchangeRequestDto): void {
    this.dataExchangeRequestApiService.approveDataExchangeRequest(row.id).subscribe({
      next: () => this.dataReloadRequired.emit()
    });
  }

  onNavigateSystemSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo?.id === packagingId);
    if (sub) { this.navigateToSubRequest(sub); }
  }

  onNavigateUnitSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo?.id === packagingId);
    if (sub) { this.navigateToSubRequest(sub); }
  }

  onNavigateComponentSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent?.id === packagingId);
    if (sub) { this.navigateToSubRequest(sub); }
  }

  onNavigateMaterialSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial?.id === packagingId);
    if (sub) { this.navigateToSubRequest(sub); }
  }

  onApproveSystemSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Closed); }
  }

  onApproveUnitSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Closed); }
  }

  onApproveComponentSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Closed); }
  }

  onApproveMaterialSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Closed); }
  }

  onReopenSystemSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.ReOpened); }
  }

  onReopenUnitSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.ReOpened); }
  }

  onReopenComponentSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.ReOpened); }
  }

  onReopenMaterialSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.ReOpened); }
  }

  onWithdrawSystemSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Deleted); }
  }

  onWithdrawUnitSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Deleted); }
  }

  onWithdrawComponentSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Deleted); }
  }

  onWithdrawMaterialSubRequest(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial?.id === packagingId);
    if (sub) { this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Deleted); }
  }

  onShowSystemSubRequestComments(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo?.id === packagingId);
    if (sub) { this.openSubRequestCommentsDialog(sub); }
  }

  onShowUnitSubRequestComments(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo?.id === packagingId);
    if (sub) { this.openSubRequestCommentsDialog(sub); }
  }

  onShowComponentSubRequestComments(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent?.id === packagingId);
    if (sub) { this.openSubRequestCommentsDialog(sub); }
  }

  onShowMaterialSubRequestComments(packagingId: number, row: DataExchangeRequestDto): void {
    const sub = row.subRequests?.find(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial?.id === packagingId);
    if (sub) { this.openSubRequestCommentsDialog(sub); }
  }

  private setSubRequestStatus(sub: DataExchangeSubRequestDto, newStatus: DataExchangeRequestStatusDto): void {
    if (newStatus === DataExchangeRequestStatusDto.ReOpened) {
      this.openReopenSubRequestDialog(sub);
      return;
    }

    const dto: UpdateDataExchangeRequestStatusDto = { status: newStatus };
    this.dataExchangeRequestApiService.updateSubRequestStatus(sub.id, dto).subscribe({
      next: () => this.dataReloadRequired.emit()
    });
  }

  private navigateToSubRequest(subRequest: DataExchangeSubRequestDto): void {
    let path: string;
    switch (subRequest.type) {
      case DataExchangeSubRequestTypeDto.PackagingUnit:
        path = PathResolveService.getDataExchangeRequestPackagingUnitPath(true, subRequest.id);
        break;
      case DataExchangeSubRequestTypeDto.PackagingSystem:
        path = PathResolveService.getDataExchangeRequestPackagingSystemPath(true, subRequest.id);
        break;
      case DataExchangeSubRequestTypeDto.PackagingComponent:
        path = this.getComponentSubRequestPath(subRequest);
        break;
      case DataExchangeSubRequestTypeDto.MultiMaterial:
        path = PathResolveService.getDataExchangeRequestMaterialPath(true, subRequest.id);
        break;
      default: return;
    }
    this.router.navigate([path]);
  }

  private getComponentSubRequestPath(subRequest: DataExchangeSubRequestDto): string {
    switch (subRequest.packagingComponent?.packagingComponentCategoryId) {
      case PackagingComponentTypesEnum.Closure:
        return PathResolveService.getDataExchangeRequestClosurePath(true, subRequest.id);
      case PackagingComponentTypesEnum.Decoration: {
        const decoType = this.componentTypeService.getComponentTypeNameById(subRequest.packagingComponent?.packagingComponentTypeId);
        return PathResolveService.getDataExchangeRequestDecorationPath(true, decoType, subRequest.id);
      }
      case PackagingComponentTypesEnum.PackagingAid:
        return PathResolveService.getDataExchangeRequestPackagingAidPath(true, subRequest.id);
      case PackagingComponentTypesEnum.Inlay:
        return PathResolveService.getDataExchangeRequestInlayPath(true, subRequest.id);
      default:
        return PathResolveService.getDataExchangeRequestMainBodyPath(true, subRequest.id);
    }
  }

  private openSubRequestCommentsDialog(sub: DataExchangeSubRequestDto): void {
    const title = this.translocoService.translate('dataManagement.dataExchangeRequests.comments.subRequestTitle');
    const data: SubRequestCommentsDialogData = { comments: sub.comments, title };
    const dialogRef = this.dialog.open(SubRequestCommentsDialogComponent, getDialogConfig(data, '500px'));
    dialogRef.afterClosed().subscribe((comment: string | undefined) => {
      if (comment) {
        this.dataExchangeRequestApiService.addSubRequestComment(sub.id, comment).subscribe({
          next: () => this.dataReloadRequired.emit()
        });
      }
    });
  }

  private openReopenSubRequestDialog(sub: DataExchangeSubRequestDto): void {
    const title = this.translocoService.translate('dataManagement.dataExchangeRequests.comments.subRequestTitle');
    const data: SubRequestCommentsDialogData = {
      comments: sub.comments,
      title,
      allowOptionalComment: true,
      confirmButtonTranslationKey: 'dataManagement.dataExchangeRequests.buttons.reopenSubRequest',
    };
    const dialogRef = this.dialog.open(SubRequestCommentsDialogComponent, getDialogConfig(data, '500px'));
    dialogRef.afterClosed().subscribe((result: SubRequestCommentsDialogResult | undefined) => {
      if (!result?.confirmed) {
        return;
      }

      const dto: UpdateDataExchangeRequestStatusDto = { status: DataExchangeRequestStatusDto.ReOpened };
      this.dataExchangeRequestApiService.updateSubRequestStatus(sub.id, dto).subscribe({
        next: () => {
          if (result.comment) {
            this.dataExchangeRequestApiService.addSubRequestComment(sub.id, result.comment).subscribe({
              next: () => this.dataReloadRequired.emit()
            });
            return;
          }

          this.dataReloadRequired.emit();
        }
      });
    });
  }

  private buildSubDataSources(row: DataExchangeRequestDto): void {
    const subs = row.subRequests ?? [];
    this.subDataSourcesCache.set(row.id, {
      systems: new MatTableDataSource(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo)
            .map(s => s.packagingSystemInfo!)
      ),
      units: new MatTableDataSource(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo)
            .map(s => s.packagingUnitInfo!)
      ),
      components: new MatTableDataSource(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent)
            .map(s => s.packagingComponent!)
      ),
      materials: new MatTableDataSource(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial)
            .map(s => s.multiMaterial as MultiMaterialCompositeImportWrapper)
      ),
      systemStatusMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo)
            .map(s => [s.packagingSystemInfo!.id, s.status])
      ),
      unitStatusMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo)
            .map(s => [s.packagingUnitInfo!.id, s.status])
      ),
      componentStatusMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent?.id != null)
            .map(s => [s.packagingComponent!.id!, s.status])
      ),
      materialStatusMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial?.id != null)
            .map(s => [s.multiMaterial!.id!, s.status])
      ),
      systemCommentCountMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingSystem && s.packagingSystemInfo)
            .map(s => [s.packagingSystemInfo!.id, s.comments?.length ?? 0])
      ),
      unitCommentCountMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingUnit && s.packagingUnitInfo)
            .map(s => [s.packagingUnitInfo!.id, s.comments?.length ?? 0])
      ),
      componentCommentCountMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.PackagingComponent && s.packagingComponent?.id != null)
            .map(s => [s.packagingComponent!.id!, s.comments?.length ?? 0])
      ),
      materialCommentCountMap: new Map(
        subs.filter(s => s.type === DataExchangeSubRequestTypeDto.MultiMaterial && s.multiMaterial?.id != null)
            .map(s => [s.multiMaterial!.id!, s.comments?.length ?? 0])
      ),
    });
  }
}
